import { DialogTitle, Stack } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { Dialog, Stepper } from '@shared/components';
import { useCallback, useState } from 'react';
import { UploadPriceSuggestionPrices } from '../UploadPriceSuggestionPrices';
import {
  AddLocalPriceSuggestionPricesTabs,
  ImportedPriceSuggestionPrice,
  PriceSuggestionPriceItem,
} from '@retail/price-suggestion/types';
import { MgSupplierItemSearchResult } from '@retail/products/types';
import { AddLocalPricesFromItemSearch } from './AddLocalPricesFromItemSearch';
import {
  EditPricesForSelectedItems,
  LocalPriceSuggestionTabs,
} from '@retail/price-suggestion/components';
import { useLocalPriceSuggestionActions } from '@retail/price-suggestion/context';
import { getISO_8601Date } from '@retail/utils';

interface Props {
  assortmentId: number;
  open: boolean;
  onClose: () => void;
}

function AddLocalPriceSuggestionsDialog({ assortmentId, open, onClose }: Props) {
  const t = useAppTFunction();

  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    { stepperLabel: t('myAssortment.actions.addItems'), stepTitle: 'ADD' },
    { stepperLabel: t('myAssortment.actions.editItems'), stepTitle: 'EDIT' },
  ];

  const [selectedTab, setSelectedTab] = useState<AddLocalPriceSuggestionPricesTabs>('ITEM_SEARCH');
  const [selectedSearchItems, setSelectedSearchItems] = useState<MgSupplierItemSearchResult[]>([]);
  const [priceSuggestionPriceItems, setPriceSuggestionPriceItems] = useState<
    PriceSuggestionPriceItem[]
  >([]);

  const handleOnContinue = () => {
    setActiveStep(1);
    setPriceSuggestionPriceItems(
      selectedSearchItems.map((item) => {
        return {
          ...item,
          mgSupplierName: item.participantAccountName,
          validFrom: new Date(),
          validTo: undefined,
          price: undefined,
        };
      }) as PriceSuggestionPriceItem[]
    );
  };

  const { createLocalPriceSuggestions } = useLocalPriceSuggestionActions();

  const handleSubmitPrices = useCallback(() => {
    const updatedItems = priceSuggestionPriceItems.map((item) => {
      return {
        mgItemNumber: item.mgItemNumber,
        mgSupplierId: item.mgSupplierId,
        price: item.price,
        validFrom: getISO_8601Date(item.validFrom),
        validTo: item.validTo ? getISO_8601Date(item.validTo) : undefined,
      };
    }) as ImportedPriceSuggestionPrice[];
    createLocalPriceSuggestions(updatedItems);
    onClose();
  }, [createLocalPriceSuggestions, onClose, priceSuggestionPriceItems]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen sx={{ p: 4 }}>
      <DialogTitle sx={{ justifyContent: 'space-between' }}>
        {t('priceSuggestion.addLocalPriceSuggestions')}
        <Stepper
          steps={steps.map((step) => step.stepperLabel)}
          activeStep={activeStep}
          labelOrientation="horizontal"
        />
      </DialogTitle>

      {activeStep === 0 && (
        <>
          <Stack px={1}>
            <LocalPriceSuggestionTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
          </Stack>

          {selectedTab === 'ITEM_SEARCH' ? (
            <AddLocalPricesFromItemSearch
              assortmentId={assortmentId}
              selectedSearchItems={selectedSearchItems}
              setSelectedSearchItems={setSelectedSearchItems}
              onContinue={handleOnContinue}
              onClose={onClose}
            />
          ) : (
            <UploadPriceSuggestionPrices assortmentId={assortmentId} onClose={onClose} />
          )}
        </>
      )}
      {activeStep === 1 && (
        <EditPricesForSelectedItems
          priceSuggestionPriceItems={priceSuggestionPriceItems}
          setPriceSuggestionPriceItems={setPriceSuggestionPriceItems}
          goBack={() => setActiveStep(0)}
          onClose={onClose}
          handleSubmitPrices={handleSubmitPrices}
        />
      )}
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(AddLocalPriceSuggestionsDialog);
